<div class="dialog-container" [class.has-test-logic]="showTestLogic">
  <div class="main-content">
    <mat-dialog-content>
      <div class="header-container">
        <h2>
          <mat-icon class="outcome-logic material-symbols-outlined">schema</mat-icon>
          {{ "campaign.outcomes.title" | translate }}
        </h2>
        <span class="spacer"></span>
        @if (!outcomesAreNotAllowed) {
          <mat-slide-toggle color="primary" [checked]="showTestLogic" (change)="toggleTestLogic()">Test logic</mat-slide-toggle>
        }
      </div>
      <p>{{ "campaign.outcomes.description" | translate }}</p>

      @if (outcomesAreNotAllowed) {
        <div class="warning-banner">
          <span>{{ "campaign.outcomes." + outcomesAreNotAllowed | translate }}</span>
        </div>
      }

      @if (loading) {
        <mat-progress-spinner mode="indeterminate" diameter="20"></mat-progress-spinner>
      }

      @if (!outcomesAreNotAllowed) {
        <div class="outcomes-container">
          @if (hasUnconfiguredAnswerQuestions) {
            <div class="warning-banner">
              <span>{{ "campaign.outcomes.unconfigured-question" | translate }}</span>
            </div>
          } @else if (hasUnconfiguredAnswerValues) {
            <div class="warning-banner">
              <span>{{ "campaign.outcomes.unconfigured-value" | translate }}</span>
            </div>
          }
          <div cdkDropList [cdkDropListData]="outcomes" (cdkDropListDropped)="onDragDrop($event)" class="outcomes-list">
            @for (outcome of outcomes; track outcome; let i = $index) {
              <mat-expansion-panel
                [expanded]="isOutcomeExpanded(i)"
                (opened)="onExpansionToggle(i, true)"
                (closed)="onExpansionToggle(i, false)"
                [cdkDragData]="outcome"
                [cdkDragDisabled]="!outcome.canDrag || loading"
                cdkDrag
                [class.draggable]="outcome.canDrag"
                [class.matched-outcome]="i === matchedOutcomeIndex"
                class="outcome-panel">
                <mat-expansion-panel-header class="right-aligned-header">
                  <mat-panel-title (keydown.space)="$event.stopPropagation()">
                    @if (outcome.canDrag) {
                      <div class="drag-handle" cdkDragHandle>
                        <mat-icon>drag_handle</mat-icon>
                      </div>
                    }
                    <span class="outcome-label">{{ "campaign.outcomes.outcome" | translate }}:</span>
                    @if (!outcome.editName) {
                      <div class="outcome-name">{{ outcome.displayName }}</div>
                    }
                    <div class="input-with-icon">
                      @if (outcome.editName) {
                        <input
                          #nameInput
                          type="text"
                          class="outcome-name-input"
                          [(ngModel)]="outcome.name"
                          (blur)="onOutcomeNameChange(outcome)"
                          (keydown.enter)="$any($event.target).blur(); $event.stopPropagation()"
                          [placeholder]="outcome.displayName?.toString() || ''"
                          (click)="$event.stopPropagation()"
                          [disabled]="loading" />
                      }
                    </div>
                    @if (!loading) {
                      <mat-icon class="outcome-name-edit-icon" (click)="editOutcomeName($event, outcome)">edit</mat-icon>
                    }
                  </mat-panel-title>
                  @if (!loading) {
                    <mat-panel-description>
                      @if (outcome.isDefault) {
                        <mat-chip color="accent" selected>Default</mat-chip>
                      }
                      @if (!outcome.isDefault) {
                        <button mat-icon-button (click)="duplicateOutcome(i); $event.stopPropagation()" class="duplicate-outcome-button">
                          <mat-icon class="material-symbols-outlined">content_copy</mat-icon>
                        </button>
                      }
                      <button mat-icon-button (click)="removeOutcome(i); $event.stopPropagation()" class="delete-outcome-button">
                        <mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
                      </button>
                    </mat-panel-description>
                  }
                </mat-expansion-panel-header>
                @if (outcome.answers.length) {
                  <p class="outcome-text">{{ "campaign.outcomes.show-this-step" | translate }}</p>
                }
                <!-- Only show answers section for non-default outcomes -->
                @if (!outcome.isDefault) {
                  <div>
                    @for (group of outcomeAnswerGroups[i]; track trackByGroupStartIndex(gi, group); let gi = $index) {
                      <!-- AND group box -->
                      <div class="answer-group">
                        @for (answer of group.answers; track trackByAnswer(j, answer); let j = $index) {
                          <div class="answer-row">
                            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="field">
                              <mat-label>{{ "campaign.outcomes.question" | translate }}</mat-label>
                              <mat-select [(ngModel)]="answer.question" (selectionChange)="onAnswerQuestionChange(answer)">
                                @for (component of inputComponents; track component) {
                                  <mat-option [value]="component.name">{{ component.displayName }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="field">
                              <mat-select [(ngModel)]="answer.operator" (selectionChange)="onAnswerOperatorChange(answer)">
                                @for (operator of OutcomeOperator | enumToArray; track operator) {
                                  <mat-option [value]="operator.value">
                                    {{ "campaign.outcomes.operators." + operator.value | translate }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="field">
                              <mat-label>Value</mat-label>
                              @if (answer.availableValues?.length > 0) {
                                @if (isMultipleSelect(answer)) {
                                  <!-- Multiple select -->
                                  <mat-select [(ngModel)]="answer.value" multiple (selectionChange)="onValueChange()">
                                    @for (value of answer.availableValues; track value) {
                                      <mat-option [value]="value">
                                        {{ value }}
                                      </mat-option>
                                    }
                                  </mat-select>
                                } @else {
                                  <!-- Single select -->
                                  <mat-select [(ngModel)]="answer.value" (selectionChange)="onValueChange()">
                                    @for (value of answer.availableValues; track value) {
                                      <mat-option [value]="value">
                                        {{ value }}
                                      </mat-option>
                                    }
                                  </mat-select>
                                }
                              } @else {
                                <!-- Text input -->
                                <input matInput [(ngModel)]="answer.value" (change)="onValueChange()" />
                              }
                            </mat-form-field>
                            <!-- Delete uses flat index: startIndex + j -->
                            <button mat-icon-button (click)="removeAnswer(outcome, group.startIndex + j)" class="delete-answer-button">
                              <mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
                            </button>
                          </div>
                          <!-- AND separator within the group (not after the last answer in the group) -->
                          @if (j < group.answers.length - 1) {
                            <div class="operator-separator">
                              <div class="operator-toggle-group">
                                @for (op of AndOrOperator | enumToArray; track op) {
                                  <button
                                    type="button"
                                    class="operator-btn"
                                    [class.operator-btn--active]="answer.andOrOperator === op.value"
                                    (click)="onAndOrOperatorChange(answer, op.value)">
                                    {{ op.value }}
                                  </button>
                                }
                              </div>
                            </div>
                          }
                        }
                      </div>
                      <!-- /.answer-group -->
                      <!-- OR separator rendered AFTER this group, bound to this group's last answer -->
                      @if (gi < outcomeAnswerGroups[i].length - 1) {
                        <div class="operator-separator">
                          <div class="operator-toggle-group">
                            @for (op of AndOrOperator | enumToArray; track op) {
                              <button
                                type="button"
                                class="operator-btn"
                                [class.operator-btn--active]="group.answers[group.answers.length - 1].andOrOperator === op.value"
                                (click)="onAndOrOperatorChange(group.answers[group.answers.length - 1], op.value)">
                                {{ op.value }}
                              </button>
                            }
                          </div>
                        </div>
                      }
                    }
                    <ng-container *ngTemplateOutlet="updateProfileField; context: { outcome: outcome }"></ng-container>
                    <button mat-stroked-button class="add-answer-button" (click)="addAnswer(outcome)">{{ "campaign.outcomes.add-answer" | translate }}</button>
                    <button mat-stroked-button class="edit-outcome-button" (click)="editOutcome(outcome)">
                      {{ "campaign.outcomes.edit-outcome" | translate }}
                    </button>
                    @if (!outcome.customDataUpdate) {
                      <button mat-stroked-button class="edit-outcome-button" (click)="activateCustomDataUpdate(outcome)">
                        {{ "campaign.outcomes.update-profile-field" | translate }}
                      </button>
                    }
                  </div>
                }
                <!-- Show message for default outcome -->
                @if (outcome.isDefault) {
                  <div class="default-outcome">
                    <div class="default-outcome-info outcome-text">
                      <mat-icon>info</mat-icon>
                      {{ "campaign.outcomes.default-outcome-description" | translate }}
                    </div>
                    <ng-container *ngTemplateOutlet="updateProfileField; context: { outcome: outcome }"></ng-container>
                    <button mat-stroked-button class="edit-outcome-button" (click)="editOutcome(outcome)">
                      {{ "campaign.outcomes.edit-outcome" | translate }}
                    </button>
                    @if (!outcome.customDataUpdate) {
                      <button mat-stroked-button class="edit-outcome-button" (click)="activateCustomDataUpdate(outcome)">
                        {{ "campaign.outcomes.update-profile-field" | translate }}
                      </button>
                    }
                  </div>
                }
              </mat-expansion-panel>
            }
            @if (missingScoresRanges) {
              <mat-expansion-panel class="outcome-panel missing-scores-outcome" [class.is-default]="missingScoresIsDefault" disabled="true" hideToggle="true">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    @if (missingScoresIsDefault) {
                      {{ "campaign.outcomes.missing-scores-default" | translate: { scores: missingScoresRanges } }}
                    } @else {
                      {{ "campaign.outcomes.missing-scores-no-mapping" | translate: { scores: missingScoresRanges } }}
                    }
                  </mat-panel-title>
                  @if (!missingScoresIsDefault) {
                    <mat-panel-description>
                      <button mat-stroked-button class="generate-button" (click)="generateMissingOutcomes(); $event.stopPropagation()">
                        {{ "campaign.outcomes.generate" | translate | uppercase }}
                      </button>
                    </mat-panel-description>
                  }
                </mat-expansion-panel-header>
              </mat-expansion-panel>
            }
            @if (showTestLogic && matchedOutcomeIndex === null) {
              <mat-expansion-panel class="outcome-panel no-match-outcome" disabled="true" hideToggle="true">
                <mat-expansion-panel-header>
                  <mat-panel-title>No outcome matched for current test logic</mat-panel-title>
                </mat-expansion-panel-header>
              </mat-expansion-panel>
            }
          </div>
        </div>
      }
    </mat-dialog-content>

    <ng-template #updateProfileField let-outcome="outcome">
      <!-- Update custom data / profile field -->
      @if (outcome?.customDataUpdate) {
        <div class="update-profile-field">
          <p class="outcome-text">{{ "campaign.outcomes.update-profile-field" | translate }}</p>
          <div class="fields">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ "campaign.outcomes.update-profile-field-name" | translate }}</mat-label>
              <mat-select [(ngModel)]="outcome.customDataUpdate.key">
                <mat-option class="create-new-field" (click)="onCreateNewField(outcome)">
                  <span>{{ "people.profileFields.addButton" | translate }}</span>
                </mat-option>
                <mat-divider></mat-divider>
                @for (field of profileFields; track field) {
                  <mat-option [value]="field.mapTo">
                    {{ field.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-icon>arrow_forward</mat-icon>

            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Value</mat-label>
              <input matInput [(ngModel)]="outcome.customDataUpdate.value" />
            </mat-form-field>

            <button mat-icon-button (click)="deactivateCustomDataUpdate(outcome)">
              <mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
            </button>
          </div>
        </div>
        <div class="update-profile-field-crm">{{ "campaign.outcomes.update-profile-field-crm" | translate }}</div>
      }
    </ng-template>

    <mat-dialog-actions>
      @if (!outcomesAreNotAllowed) {
        <button mat-stroked-button class="secondary-button" (click)="addOutcome()" [disabled]="loading">
          {{ "campaign.outcomes.add-outcome" | translate }}
        </button>
        <button mat-stroked-button class="secondary-button" (click)="openGenerator()" [disabled]="loading">
          {{ "campaign.outcomes.generator.title" | translate }}
        </button>
        @if (outcomes && !hasDefaultOutcome()) {
          <button mat-stroked-button class="secondary-button" (click)="addDefaultOutcome()" [disabled]="loading">
            {{ "campaign.outcomes.add-default-outcome" | translate }}
          </button>
        }
      }
      <span class="spacer"></span>
      <button mat-stroked-button class="secondary-button" (click)="cancel()">{{ "generic.close" | translate }}</button>
      @if (!outcomesAreNotAllowed) {
        <button mat-flat-button color="primary" (click)="close()" [disabled]="loading || !saveIsAllowed">
          {{ "generic.save" | translate }}
        </button>
      }
    </mat-dialog-actions>
  </div>

  @if (showTestLogic) {
    <app-outcome-test-logic
      [outcomes]="outcomes"
      [inputComponents]="inputComponents"
      [scoreInput]="scoreInput"
      [minScore]="minScore"
      [maxScore]="maxScore"
      [hasScore]="hasScore"
      [updateTrigger]="logicUpdateTrigger"
      (matchedOutcomeIndexChange)="onMatchedOutcomeIndexChange($event)"
      (close)="closeTestLogic()"></app-outcome-test-logic>
  }
</div>
